/**
 * Reaction summary helpers for the web chat chips.
 *
 * Server payloads (REST and `reaction_updated`) include `reactors:
 * [{ person_id, name, reacted_at }]`. The chip hover label is those names.
 */

export function reactorEntries(reaction) {
  if (!reaction || !Array.isArray(reaction.reactors)) return []
  return reaction.reactors.filter(entry => entry && String(entry.name || '').trim())
}

/** Names shown when hovering a reaction chip. Empty when the payload has no reactors. */
export function reactionHoverLabel(reaction) {
  return reactorEntries(reaction).map(entry => String(entry.name).trim()).join(', ')
}

/**
 * Cable `reaction_updated` is one payload for the whole room, so
 * `reacted_by_me` arrives false for every viewer. Derive it from `person_ids`
 * and keep `reactors` (names) intact. Mirrors chat-shared `withMyReaction`,
 * comparing ids numerically so string and number ids still match.
 */
export function reactionsForViewer(reactions, personId) {
  const mine = Number(personId)
  const list = Array.isArray(reactions) ? reactions : []
  return list.map(reaction => {
    const reacted = Array.isArray(reaction.person_ids)
      ? reaction.person_ids.some(id => Number(id) === mine)
      : !!reaction.reacted_by_me
    return { ...reaction, reacted_by_me: reacted }
  })
}

/**
 * Optimistic add/remove that keeps `reactors` (names) and `person_ids` in sync
 * with the count, so hover still lists who reacted before the cable echo.
 *
 * @param {Array} reactions
 * @param {string} emoji
 * @param {number} personId
 * @param {string} personName
 * @param {string} [reactedAt]
 */
export function optimisticReactionToggle(reactions, emoji, personId, personName, reactedAt = new Date().toISOString()) {
  const mine = Number(personId)
  const list = Array.isArray(reactions) ? reactions : []
  const entry = list.find(item => item.emoji === emoji)
  const reacted = entry
    ? (Array.isArray(entry.person_ids)
      ? entry.person_ids.some(id => Number(id) === mine)
      : !!entry.reacted_by_me)
    : false

  if (!entry) {
    return [
      ...list,
      {
        emoji,
        count: 1,
        person_ids: [mine],
        reacted_by_me: true,
        reactors: [{ person_id: mine, name: personName || '', reacted_at: reactedAt }],
      },
    ]
  }

  if (reacted) {
    return list.flatMap(item => {
      if (item.emoji !== emoji) return [item]
      const hadIds = Array.isArray(item.person_ids)
      const personIds = hadIds ? item.person_ids.filter(id => Number(id) !== mine) : item.person_ids
      const reactors = (item.reactors ?? []).filter(reactor => Number(reactor.person_id) !== mine)
      const count = hadIds ? personIds.length : Math.max(0, item.count - 1)
      if (count <= 0) return []
      return [{ ...item, person_ids: personIds, reactors, count, reacted_by_me: false }]
    })
  }

  return list.map(item => {
    if (item.emoji !== emoji) return item
    const hadIds = Array.isArray(item.person_ids)
    const personIds = hadIds ? [...item.person_ids, mine] : item.person_ids
    const reactors = [
      ...(item.reactors ?? []).filter(reactor => Number(reactor.person_id) !== mine),
      { person_id: mine, name: personName || '', reacted_at: reactedAt },
    ]
    const count = hadIds ? personIds.length : item.count + 1
    return { ...item, person_ids: personIds, reactors, count, reacted_by_me: true }
  })
}
